import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { customStorage, isNullOrEmptyString } from "../services/CommonService";
import { STO_PLAYER_ID, STO_PLAYER_NAME, STO_ROOM_ID } from "../constants/commonConstants";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Box, Button, Card, CardContent, Typography, DialogActions, DialogContent, DialogTitle, TextField, IconButton } from "@mui/material";
import Dialog from '../components/ActionDialog';
import { AddBox, Close, Edit, Keyboard, Login, Refresh, Shuffle } from "@mui/icons-material";
import WarningDialog from "../components/WarningDialog";
import { autoRejoinRoom, createNewRoom, joinRoom, playRandom } from "../services/GameService";
import { getMyRoom } from "../services/AuthService";
import RoomConflictDialog from '../components/RoomConflictDialog';
import JoinApprovalDialog from '../components/JoinApprovalDialog';
import { rememberRoomMembership, rememberRoomCredential } from '../services/RoomSession';
import './GameSelection.scss';
import GameSettingsFields, { DEFAULT_MONEY_CARDS, DEFAULT_ROOM_RULES } from '../components/GameSettingsFields';
import SubscriptionGate from '../components/SubscriptionGate';
import SubscriptionDialog from '../components/SubscriptionDialog';
import { useSubscription } from '../context/SubscriptionContext';
import { DEFAULT_CARD_BACK_DESIGN } from '../services/CardBackPreference';
import { getEnabledGameCount } from '../services/GameAvailability';
import { getGameErrorMessage } from '../services/ApiError';


const GameHome = () => {
  const { t } = useTranslation();
  const { refresh: refreshSubscription, feature } = useSubscription();
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [tempPlayerName, setTempPlayerName] = useState("");
  const [tempRoomId, setTempRoomId] = useState("");
  const [playerName, setPlayerName] = useState("");
  const [deckCount, setDeckCount] = React.useState(2);
  const [jokerPairs, setJokerPairs] = React.useState(2);
  const [cardsPerPlayer, setCardsPerPlayer] = React.useState(13);
  const [moneyCards, setMoneyCards] = useState({ ...DEFAULT_MONEY_CARDS });
  const [maxWaitSeconds, setMaxWaitSeconds] = useState(null);
  const [cardBackDesign, setCardBackDesign] = useState(DEFAULT_CARD_BACK_DESIGN);
  const [roomRules, setRoomRules] = useState({ ...DEFAULT_ROOM_RULES });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openPlayerNameDialog, setOpenPlayerNameDialog] = useState(false);
  const [showRoomIdDialog, setShowRoomIdDialog] = useState(false);
  const [openGameSettingDialog, setOpenGameSettingDialog] = useState(false);
  const [btnActionName, setBtnActionName] = useState('name');
  const playerNameInputRef = useRef(null);
  const roomIdInputRef = useRef(null);
  const [myRoom, setMyRoom] = useState(null); // { roomId, playerId } or null
  const [myRoomLoading, setMyRoomLoading] = useState(false);
  const [roomConflict, setRoomConflict] = useState(null);
  const [pendingJoin, setPendingJoin] = useState(null);
  const lobbySession = useRef(null);
  const maxPlayerNameLength = 8;
  const showAllGames = getEnabledGameCount(feature) > 1;

  const errors = {
    invalidRoomId: t('gameHome.errors.invalidRoomId'),
    joinRoomError: t('gameHome.errors.joinRoomError'),
    createRoomError: t('gameHome.errors.createRoomError')
  }


  useEffect(() => {
    const current = { active: true };
    lobbySession.current = current;
    setLoading(false);
    setError(null);
    setPendingJoin(null);
    setRoomConflict(null);
    setMyRoom(null);
    setMyRoomLoading(false);
    setTempRoomId('');
    setOpenPlayerNameDialog(false);
    setShowRoomIdDialog(false);
    setOpenGameSettingDialog(false);
    const init = async () => {
      // Player name is chosen by the user on this page (or inherited from a
      // previous room session) and is never derived from the account username.
      const pName = customStorage.getItem(STO_PLAYER_NAME) || '';
      setPlayerName(pName);
      if (pName) {
        customStorage.setItem(STO_PLAYER_NAME, pName);
      }

      const isRoomIdValid = /^\d{4,12}$/.test(roomId || '');
      if (isRoomIdValid) {
        setTempRoomId(roomId);
        if (pName) {
          setTempPlayerName(pName);
          await joinGameRoom(roomId, pName, null, { invitation: true });
        } else {
          setBtnActionName('joinInvite');
          setOpenPlayerNameDialog(true);
        }
      } else {
        // No room id in URL — fetch the authoritative rejoinable room from the server.
        try {
          setMyRoomLoading(true);
          const data = await getMyRoom();
          if (current.active && data?.roomId && data.autoRejoinEligible) {
            const recovered = await autoRejoinRoom(data.roomId, pName || 'Player');
            if (current.active) {
              customStorage.setItem(STO_PLAYER_ID, recovered.playerId);
              customStorage.setItem(STO_ROOM_ID, recovered.roomId);
              navigate(`/play/${recovered.roomId}/${recovered.playerId}`, { replace: true });
            }
          } else if (current.active) setMyRoom(data && data.roomId ? data : null);
        } catch (err) {
          if (current.active) {
            setMyRoom(null);
            if (['ROOM_NOT_FOUND', 'AUTO_REJOIN_EXPIRED'].includes(err?.response?.data?.code))
              setError(t('roomMembership.roomMissingHelp'));
          }
        } finally {
          if (current.active) setMyRoomLoading(false);
        }
      }
    };
    init();
    return () => { current.active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, roomId]);

  useEffect(() => {
    const timeOut = focusOnDialogOpen(openPlayerNameDialog, playerNameInputRef) || focusOnDialogOpen(showRoomIdDialog, roomIdInputRef);
    return () => {
      if (timeOut) {
        clearTimeout(timeOut)
      }
    };
  }, [openPlayerNameDialog, showRoomIdDialog]);

  const focusOnDialogOpen = (dialogOpen, inputRef) => {
    if (dialogOpen) {
      const timeOut = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
      return timeOut;
    }
    return null;
  };
  // const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  const handleCreateRoom = async (options = {}, settings = { playerName, deckCount, jokerPairs, cardsPerPlayer, moneyCards, maxWaitSeconds, cardBackDesign, roomRules }) => {
    const current = lobbySession.current;
    const requestId = options.requestId || crypto.randomUUID();
    try {
      setLoading(true);
      const response = await createNewRoom(settings.playerName || '', settings.deckCount || '', settings.jokerPairs ?? 2,
        { ...options, requestId, cardsPerPlayer: settings.cardsPerPlayer, moneyCards: settings.moneyCards, maxWaitSeconds: settings.maxWaitSeconds,
          cardBackDesign: settings.cardBackDesign, ...settings.roomRules });
      if (!current?.active) return;
      if (response) {
        customStorage.setItem(STO_PLAYER_ID, response.playerId);
        customStorage.setItem(STO_ROOM_ID, response.roomId);
        customStorage.setItem(STO_PLAYER_NAME, settings.playerName);
        setRoomConflict(null);
        navigate(`/play/${response.roomId}/${response.playerId}`);
      } else {
        setError(errors.createRoomError)
      }
    } catch (err) {
      if (!current?.active) return;
      if (err?.response?.data?.code === 'SUBSCRIPTION_FEATURE_LOCKED') {
        void refreshSubscription(); setSubscribeOpen(true); setError(t('subscription.required'));
      } else if (err?.response?.data?.code === 'ACTIVE_ROOM_CONFLICT') {
        const conflict = err.response.data;
        setRoomConflict({ ...conflict, retry: () => handleCreateRoom({ requestId, switchConfirmationToken: conflict.switchConfirmationToken }, settings) });
      } else {
        setRoomConflict(null);
        setError(err?.response?.data?.code?.startsWith('STALE_ROOM') ? t('roomMembership.staleConfirmation') : errors.createRoomError);
      }
    } finally {
      if (current?.active) {
        setOpenGameSettingDialog(false);
        setLoading(false);
      }
    }
  };

  const handleConfirmJoinRoom = () => {
    setBtnActionName('join');
    if (isNullOrEmptyString(playerName)) {
      setOpenPlayerNameDialog(true);
    } else {
      setShowRoomIdDialog(true);
    }
  }

  const handleJoinRoom = async () => {
    if (loading || !/^\d{4,12}$/.test(tempRoomId)) return;
    const current = lobbySession.current;
    // setRoomId(tempRoomId);
    await joinGameRoom(tempRoomId, playerName, null);
    if (!current?.active) return;
    setTempRoomId(roomId || "");
    setShowRoomIdDialog(false);
  };

  const handleRejoinRoom = async () => {
    if (!myRoom || !myRoom.roomId) return;
    // Rejoin with the player name the user last used (stored locally). The
    // server keeps the player's slot bound to their id, so the name is only
    // a display value. Username is no longer used for rooms.
    const pName = customStorage.getItem(STO_PLAYER_NAME) || playerName || '';
    await joinGameRoom(myRoom.roomId, pName, myRoom.playerId || null);
  };

  const joinGameRoom = async (rmId, pName, pId, options = {}) => {
    const current = lobbySession.current;
    const requestId = options.requestId || crypto.randomUUID();
    try {
      setLoading(true);
      const response = await joinRoom(rmId || '', pName || '', pId || '', { ...options, requestId });
      if (!current?.active) return;
      if (response?.status === 'Pending') {
        setRoomConflict(null);
        setPendingJoin(response);
      } else if (response?.status) {
        handleJoinOutcome(response);
      } else if (response) {
        customStorage.setItem(STO_PLAYER_ID, response.playerId);
        customStorage.setItem(STO_ROOM_ID, rmId);
        setRoomConflict(null);
        navigate(`/play/${rmId}/${response.playerId}`);
      }
    } catch (err) {
      if (!current?.active) return;
      if (['SUBSCRIPTION_FEATURE_LOCKED', 'EXPIRED_HOST_REJOIN'].includes(err?.response?.data?.code)) {
        void refreshSubscription(); setSubscribeOpen(true); setError(t(err.response.data.code === 'EXPIRED_HOST_REJOIN' ? 'subscription.expiredHost' : 'subscription.required'));
      } else if (err?.response?.data?.code === 'ACTIVE_ROOM_CONFLICT') {
        const conflict = err.response.data;
        setRoomConflict({ ...conflict, retry: () => joinGameRoom(rmId, pName, pId, { requestId, switchConfirmationToken: conflict.switchConfirmationToken }) });
      } else if (err?.response?.data?.code?.startsWith('STALE_ROOM')) {
        setRoomConflict(null);
        setError(t('roomMembership.staleConfirmation'));
      } else if (err?.response?.data?.code === 'ROOM_NOT_FOUND') {
        setError(t('roomMembership.roomMissingHelp'));
      } else if (['RANDOM_ROOM_MANUAL_JOIN_DISABLED', 'JOIN_REQUEST_PENDING', 'ROOM_UNAVAILABLE'].includes(err?.response?.data?.code)) {
        setError(t(err.response.data.code === 'RANDOM_ROOM_MANUAL_JOIN_DISABLED' ? 'roomEntry.manualDisabled'
          : err.response.data.code === 'JOIN_REQUEST_PENDING' ? 'roomEntry.pendingError' : 'roomEntry.roomUnavailable'));
      } else if (err?.response?.status === 400) {
        setError(getGameErrorMessage(err, t, errors.joinRoomError));
      }
      else {
        setError(errors.joinRoomError);
      }
    } finally {
      if (current?.active) setLoading(false);
    }
  }

  const handleJoinOutcome = response => {
    setPendingJoin(null);
    if (response.status === 'Approved') {
      rememberRoomMembership(response.roomId, response.membershipVersion);
      rememberRoomCredential(response.roomId, response.roomCredential);
      customStorage.setItem(STO_PLAYER_ID, response.playerId);
      customStorage.setItem(STO_ROOM_ID, response.roomId);
      navigate(`/play/${response.roomId}/${response.playerId}`);
    } else if (response.status !== 'Canceled' || response.code) {
      setError(t(response.code === 'JOIN_HOST_CHANGED' ? 'roomEntry.hostChanged'
        : response.code === 'E_MSG_0005' ? 'roomEntry.roomFull'
        : response.code === 'ROOM_NOT_FOUND' ? 'roomMembership.roomMissingHelp'
        : response.code === 'STALE_ROOM_CONFIRMATION' ? 'roomMembership.staleConfirmation'
        : response.status === 'Expired' ? 'roomEntry.expired' : 'roomEntry.rejected'));
    }
  };

  const handleRandom = async (options = {}, name = playerName) => {
    if (!name) { setBtnActionName('random'); setOpenPlayerNameDialog(true); return; }
    const requestId = options.requestId || crypto.randomUUID();
    const current = lobbySession.current;
    try {
      setLoading(true);
      const response = await playRandom(name, { ...options, requestId });
      if (!current?.active) return;
      customStorage.setItem(STO_PLAYER_ID, response.playerId);
      customStorage.setItem(STO_ROOM_ID, response.roomId);
      customStorage.setItem(STO_PLAYER_NAME, name);
      setRoomConflict(null);
      navigate(`/play/${response.roomId}/${response.playerId}`);
    } catch (err) {
      if (!current?.active) return;
      if (err?.response?.data?.code === 'SUBSCRIPTION_FEATURE_LOCKED') {
        void refreshSubscription(); setSubscribeOpen(true); setError(t('subscription.required'));
      } else if (err?.response?.data?.code === 'ACTIVE_ROOM_CONFLICT') {
        const conflict = err.response.data;
        setRoomConflict({ ...conflict, retry: () => handleRandom({ requestId, switchConfirmationToken: conflict.switchConfirmationToken }, name) });
      } else {
        setRoomConflict(null);
        setError(t(err?.response?.data?.code?.startsWith('STALE_ROOM') ? 'roomMembership.staleConfirmation'
          : err?.response?.data?.code === 'JOIN_REQUEST_PENDING' ? 'roomEntry.pendingError' : 'roomEntry.matchError'));
      }
    } finally { if (current?.active) setLoading(false); }
  };

  const handleCreateRoomClick = () => {
    setBtnActionName('create');
    if (isNullOrEmptyString(playerName)) {
      setOpenPlayerNameDialog(true);
    } else {
      setOpenGameSettingDialog(true);
    }
  };

  const handleSavePlayerName = async () => {
    if (loading || !tempPlayerName?.trim() || tempPlayerName.trim().length > maxPlayerNameLength) return;
    const current = lobbySession.current;
    setPlayerName(tempPlayerName?.trim());
    customStorage.setItem(STO_PLAYER_NAME, tempPlayerName?.trim());
    if (btnActionName === 'joinInvite') {
      await joinGameRoom(tempRoomId, tempPlayerName?.trim(), null, { invitation: true });
      if (!current?.active) return;
    }
    setTempPlayerName("");
    setOpenPlayerNameDialog(false);
    if (btnActionName === 'create') {
      setOpenGameSettingDialog(true);
    }
    if (btnActionName === 'join') {
      setShowRoomIdDialog(true);
    }
    if (btnActionName === 'random') await handleRandom({}, tempPlayerName?.trim());
  };

  const handleSaveSettings = () => {
    if (loading) return;
    setOpenGameSettingDialog(false);
    handleCreateRoom(); // Call the room creation function after setting deck count
  };

  const handleChangeName = () => {
    setBtnActionName('name')
    setOpenPlayerNameDialog(true);
  }

  const isRejoinBtnAvailable = () => {
    return !!myRoom?.roomId && !myRoomLoading;
  };

  const commonStyles = {
    fullHeightCenter: {
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "var(--app-bg)",
    },
    card: {
      width: { xs: "80%" },
      minWidth: { sm: "18.75rem" },
      minHeight: { sm: "12.5rem" },
      padding: { xs: "1.25rem" },
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      color: "var(--app-action-text)",
      boxShadow: "0 0 0 0.313rem var(--app-shadow)",
      border: "0.25rem solid var(--app-border)",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
      "&:hover": {
        transform: "translateY(-0.625rem)",
        boxShadow: "0 0 0 0.5rem var(--app-shadow)",
        cursor: "pointer",
      },
    },
    buttonText: {
      textAlign: "center",
      textTransform: "none",
    },
  };


  return (
    <>
      <RoomConflictDialog conflict={roomConflict} busy={loading} onCancel={() => setRoomConflict(null)} onConfirm={() => roomConflict?.retry()} />
      <SubscriptionDialog open={subscribeOpen} onClose={() => setSubscribeOpen(false)} />
      {/* <AppMetaData /> */}
      <Box className="app-page lobby-page" sx={{
        display: 'flex',
        minHeight: '100vh',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Box className="lobby-shell" sx={commonStyles.fullHeightCenter}>
          <Box className="lobby-content" sx={{
            height: "100%",
          }}>
            <div className="poker-lobby-title">
              <Typography component="h1" variant="h5">{t('gameSelection.pokerLobby')}</Typography>
              {showAllGames && <Button onClick={() => navigate('/games')}>{t('gameSelection.allGames')}</Button>}
            </div>
            <LoadingSpinner show={loading} />
            <Box className="lobby-player-greeting" sx={{ display: "block", textAlign: "center", backgroundColor: "var(--app-surface)", color: "var(--app-text)" }}>
              {/* Player Name Display */}
              {playerName ?
                (<Typography variant="h6" color="text.primary" sx={commonStyles.buttonText}>
                  <Typography variant="h6" component={"span"} color="primary.main">{t('gameHome.hello')}</Typography> {playerName} <Button aria-label={t('gameRoom.changeYourName')} onClick={handleChangeName}><Edit sx={{ fontSize: 30, marginBottom: "0.25rem", marginLeft: "0.313rem" }} /></Button>
                </Typography>)
                : (<Button onClick={handleChangeName}>
                  <Typography variant="h6" color="text.primary" sx={commonStyles.buttonText}>
                    {t('gameHome.setYourName')}: <Keyboard sx={{ fontSize: 30, marginBottom: "0.25rem" }} />
                  </Typography>
                </Button>)}
            </Box>
            <Box
              className="lobby-actions poker-lobby-actions"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "stretch",
                gap: 4,
                p: 2,
                backgroundColor: "var(--app-bg)",
              }}
            >
              {/* Create Room Card */}
              <SubscriptionGate feature="createRoom"><Card
                className="lobby-action-card create-room-card"
                sx={{
                  ...commonStyles.card,
                  backgroundColor: "var(--app-accent)",
                }}
                onClick={handleCreateRoomClick}
              >
                <AddBox sx={{ fontSize: 50, mb: 2 }} />
                <CardContent>
                  <Typography variant="h5" align="center" sx={{ fontWeight: "bold" }}>
                    {t('gameHome.createRoom')}
                  </Typography>
                </CardContent>
              </Card></SubscriptionGate>

              {/* Join Room Card */}
              <SubscriptionGate feature="joinRoom"><Card
                className="lobby-action-card join-room-card"
                sx={{
                  ...commonStyles.card,
                  backgroundColor: "var(--app-info)",
                }}
                onClick={handleConfirmJoinRoom}
              >
                <Login sx={{ fontSize: 50, mb: 2 }} />
                <CardContent>
                  <Typography variant="h5" align="center" sx={{ fontWeight: "bold" }}>
                    {t('gameHome.joinRoom')}
                  </Typography>
                </CardContent>
              </Card></SubscriptionGate>

              {/* Dialog for Player Name */}
              <SubscriptionGate feature="randomPlay"><Card className="lobby-action-card random-room-card" sx={commonStyles.card}>
                <Button onClick={() => handleRandom()} sx={{ width: '100%', height: '100%', color: 'inherit', flexDirection: 'column', flex: 1 }}>
                  <Shuffle sx={{ fontSize: 50, mb: 2 }} />
                  <Typography variant="h5" align="center" sx={{ fontWeight: 'bold' }}>{t('roomEntry.playRandom')}</Typography>
                </Button>
              </Card></SubscriptionGate>
              <Dialog fullWidth={true} open={openPlayerNameDialog} PaperProps={{ component: 'form', onSubmit: event => { event.preventDefault(); handleSavePlayerName(); } }}>
                <DialogTitle>
                  {t('gameHome.enterPlayerName')} {btnActionName === 'joinInvite' ? t('gameHome.toJoinRoom', { roomId }) : ''}
                  <IconButton
                    aria-label="close"
                    onClick={() => { setTempPlayerName(''); setOpenPlayerNameDialog(false); }}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <Close />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <TextField
                    inputRef={playerNameInputRef}
                    autoFocus
                    autoComplete="off"
                    margin="dense"
                    label={t('gameHome.playerName')}
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={tempPlayerName}
                    onChange={(e) => setTempPlayerName(e.target.value)}
                    error={isNullOrEmptyString(tempPlayerName) || tempPlayerName.length > maxPlayerNameLength}
                    helperText={
                      isNullOrEmptyString(tempPlayerName)
                        ? t('gameHome.playerNameRequired')
                        : tempPlayerName.length > maxPlayerNameLength
                          ? t('gameHome.playerNameMaxLength', { max: maxPlayerNameLength })
                          : ""
                    }
                  />
                </DialogContent>
                <DialogActions>
                  <Button type="submit" disabled={loading} className="app-btn" variant="contained"
                  >
                    {btnActionName === 'joinInvite' ? t('gameHome.saveAndJoin') : t('common.save')}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Dialog for RoomId */}
              <Dialog fullWidth={true} open={showRoomIdDialog} aria-labelledby="roomId-dialog-title" PaperProps={{ component: 'form', onSubmit: event => { event.preventDefault(); handleJoinRoom(); } }}>
                <DialogTitle id="roomId-dialog-title">
                  {t('gameHome.enterRoomId')}
                  <IconButton
                    aria-label="close"
                    onClick={() => { setTempRoomId(roomId || ''); setShowRoomIdDialog(false); }}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <Close />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <TextField
                    inputMode="numeric"
                    inputRef={roomIdInputRef}
                    autoFocus
                    autoComplete="off"
                    margin="dense"
                    label={t('gameHome.roomId')}
                    inputProps={{ style: { textAlign: 'center', fontVariantNumeric: 'tabular-nums' }, inputMode: 'numeric' }}
                    type="tel"
                    fullWidth
                    variant="outlined"
                    value={tempRoomId}
                    onChange={(e) => setTempRoomId(e.target.value)}
                    error={!/^\d{4,12}$/.test(tempRoomId)}
                    helperText={
                      !tempRoomId
                        ? t('gameHome.roomIdRequired')
                        : !/^\d{4,12}$/.test(tempRoomId)
                          ? t('gameHome.roomIdMustBeDigits')
                          : ""
                    }
                  />
                </DialogContent>
                <DialogActions>
                  <Button type="submit" disabled={loading} className="app-btn" variant="contained"
                  >
                    {t('common.join')}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Dialog for Game Settings */}
              <Dialog fullWidth={true} open={openGameSettingDialog} PaperProps={{ component: 'form', onSubmit: event => { event.preventDefault(); handleSaveSettings(); } }}>
                <DialogTitle>
                  {t('gameHome.chooseSettings')}
                  <IconButton
                    aria-label="close"
                    onClick={() => setOpenGameSettingDialog(false)}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <Close />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <GameSettingsFields deckCount={deckCount} jokerPairs={jokerPairs} cardsPerPlayer={cardsPerPlayer} moneyCards={moneyCards} maxWaitSeconds={maxWaitSeconds} cardBackDesign={cardBackDesign} roomRules={roomRules}
                    onChange={settings => { setDeckCount(settings.deckCount); setJokerPairs(settings.jokerPairs);
                      setCardsPerPlayer(settings.cardsPerPlayer); setMoneyCards(settings.moneyCards); setMaxWaitSeconds(settings.maxWaitSeconds);
                      setCardBackDesign(settings.cardBackDesign); setRoomRules(settings.roomRules); }} />
                </DialogContent>
                <DialogActions>
                  <Button type="submit" disabled={loading} className="app-btn" variant="contained">{t('common.create')}</Button>
                </DialogActions>
              </Dialog>

              <WarningDialog
                fullWidth={true}
                open={!!error}
                onClose={() => setError("")}
                title={t('gameHome.gameInformation')}
                warningText={error}
              />
            </Box>
          </Box>
          {isRejoinBtnAvailable() && <Box className="rejoin-room-action">
            <SubscriptionGate feature="rejoin"><Card className="rejoin-room-card" sx={{
              backgroundColor: "var(--app-surface)",
              borderRadius: "1rem",
              boxShadow: "0 0.5rem 1rem var(--app-shadow)",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-0.5rem)",
                boxShadow: "0 0.75rem 1.5rem var(--app-shadow)",
              },
            }}>
              <Button
                sx={{
                  textTransform: "none",
                  color: "var(--app-accent)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontWeight: "bold",
                  padding: { xs: "0.5rem 0.75rem", sm: "0.625rem 0.875rem", md: "0.75rem 1rem" },
                  // backgroundColor: "#16213e",
                  borderRadius: "1rem",
                  "&:hover": {
                    backgroundColor: "var(--app-glow)",
                  },
                }}
                onClick={handleRejoinRoom}
              >
                <Refresh sx={{
                  fontSize: { xs: "1.25rem", sm: "1.875rem", md: "2.5rem" },
                  color: "var(--app-accent)",
                  marginRight: "0.625rem",
                }} />
                <Typography sx={{
                  fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                  whiteSpace: "nowrap",
                  wordBreak: "keep-all",
                }}>
                  {t('gameHome.rejoinLastRoom')}
                </Typography>
              </Button>
            </Card></SubscriptionGate>
          </Box>}
          <JoinApprovalDialog request={pendingJoin} onOutcome={handleJoinOutcome} />
        </Box>
      </Box>
    </>
  );
};

export default GameHome;
